import React, { useState, useEffect } from "react";
import { getAllActeursList } from "../../services/acteursServices";
import {
  assignActeursToUser,
  removeActeurFromUser,
} from "../../services/userServices";
import Button from "../../components/Button/Button";
import { toast } from "react-toastify";
import { FaTimes } from "react-icons/fa";

const ManageAdherentsModal = ({ isOpen, onClose, userId, refresh }) => {
  const [adherents, setAdherents] = useState([]);
  const [filter, setFilter] = useState("");
  const [filteredAdherents, setFilteredAdherents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Chargement des acteurs (liste complète)
  useEffect(() => {
    if (!isOpen) return;

    const fetchActeurs = async () => {
      try {
        setLoading(true);
        const data = await getAllActeursList();
        setAdherents(data);
        setFilteredAdherents(data);
      } catch (error) {
        console.error("Erreur lors du chargement des adhérents :", error);
        toast.error("Erreur lors du chargement des adhérents");
      } finally {
        setLoading(false);
      }
    };

    fetchActeurs();
  }, [isOpen]);

  useEffect(() => {
    const lower = filter.toLowerCase();
    setFilteredAdherents(
      adherents.filter(
        (a) =>
          (a.nom && a.nom.toLowerCase().includes(lower)) ||
          (a.prenom && a.prenom.toLowerCase().includes(lower))
      )
    );
  }, [filter, adherents]);

  const handleAddAdherent = async (adherentId) => {
    if (!userId) return;
    try {
      await assignActeursToUser(userId, adherentId);
      if (refresh) await refresh();
      toast.success("Adhérent assigné avec succès");
    } catch (error) {
      console.error("Erreur lors de l'assignation de l'adhérent :", error);
      toast.error("Erreur lors de l'assignation de l'adhérent");
    }
  };

  const handleRemoveAdherent = async (adherentId) => {
    if (!userId) return;
    try {
      await removeActeurFromUser(userId, adherentId);
      if (refresh) await refresh();
      toast.success("Adhérent retiré avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'adhérent :", error);
      toast.error("Erreur lors de la suppression de l'adhérent");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
      <div className="w-[95vw] max-w-3xl max-h-[90vh] rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/70 dark:border-slate-700/70 overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 text-white">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Gérer les acteurs liés à l’utilisateur
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="px-4 sm:px-5 pt-4 pb-5 flex-1 flex flex-col gap-3 text-slate-800 dark:text-slate-100">
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mb-1">
            Recherchez un adhérent par <span className="font-semibold">nom</span> ou{" "}
            <span className="font-semibold">prénom</span>, puis utilisez les boutons{" "}
            <span className="font-semibold">Ajouter</span> /{" "}
            <span className="font-semibold">Retirer</span> pour gérer les liens avec
            l’utilisateur sélectionné.
          </p>

          {/* Recherche */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Rechercher par nom ou prénom"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
              />
            </div>
            <div className="sm:w-auto">
              <Button
                text="Fermer"
                onClick={onClose}
                className="btn-ghost !rounded-xl !px-4 !py-2 text-sm"
              />
            </div>
          </div>

          {/* Liste des adhérents */}
          <div className="mt-2 flex-1 overflow-y-auto rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/60">
            {loading ? (
              <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-300">
                Chargement des adhérents…
              </div>
            ) : filteredAdherents.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-300">
                Aucun adhérent ne correspond à cette recherche.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-800 text-sm">
                {filteredAdherents.map((adherent) => (
                  <li
                    key={adherent.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-white/80 dark:hover:bg-slate-800/70"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-[0.8rem] sm:text-sm truncate">
                        {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
                      </div>
                      {adherent.club && (
                        <div className="text-[0.7rem] text-slate-500 dark:text-slate-400 truncate">
                          {adherent.club}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-nowrap gap-1">
                      <button
                        onClick={() => handleAddAdherent(adherent.id)}
                        className="px-2.5 py-1 text-[0.7rem] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm"
                      >
                        Ajouter
                      </button>
                      <button
                        onClick={() => handleRemoveAdherent(adherent.id)}
                        className="px-2.5 py-1 text-[0.7rem] rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold shadow-sm"
                      >
                        Retirer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAdherentsModal;
